import React from 'react';
import { Link } from 'react-router-dom';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import styled from 'styled-components';

import MetadataUtils from '../utils/utils-metadata';
import withLanguageContext from '../../contexts/language/withLanguageContext';
import withCampaignContext from '../../contexts/campaign/withCampaignContext';
import sortBy from 'lodash/sortBy';
import {
    LOANFORMAT_ALL_OBJECT,
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../../store/loanFormat/actions';
import { LENDING_MODEL_OCOU, LENDING_MODEL_SIM, UNLIMITED_USAGE_LIMIT } from '../../store/constants';
import { LocationDescriptor } from 'history';
import { LendingModel } from '../../../models/PriceTier';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const StUsageConditionLine = styled.p`
    margin-bottom: 1px !important;
`;

StUsageConditionLine.displayName = 'ProductUsageConditions__StUsageConditionLine';
const StUsageConditionContainer = styled.div`
    margin-bottom: 5px;
`;

StUsageConditionContainer.displayName = 'ProductUsageConditions__StUsageConditionContainer';

const StUsageConditionElement = styled.div`
    margin-bottom: 10px;
`;
StUsageConditionElement.displayName = 'ProductUsageConditions__StUsageConditionElement';

function mapTermLimit(termLimit, t: TFunction) {
    if (!isNumber(termLimit) || termLimit < 0) {
        return '';
    }
    if (termLimit >= 365) {
        const termLimitYears = Math.round(termLimit / 365);
        return t('components.productUsageConditions.termLimitYear', { count: termLimitYears });
    }
    if (termLimit < 365 && termLimit > 0) {
        return t('components.productUsageConditions.termLimitDay', { count: termLimit });
    } else {
        return t('components.productUsageConditions.noTermLimit');
    }
}

function mapUsageLimit(usageLimit, t: TFunction) {
    if (!isNumber(usageLimit) || usageLimit < 0) {
        return '';
    }
    if (usageLimit === 0) {
        return '';
    }
    return t('components.productUsageConditions.loanLimit', { usageLimit: usageLimit });
}

function setUsageConditionFilterLink(productClass, loanFormatKey, usageCondition, language) {
    const filter = { multiUsageCondition: { include: [usageCondition] } };

    return MetadataUtils.createMetaDataLink(productClass, filter, { loanFormatKey, language });
}

type UsageConditions = {
    lendingModel: LendingModel | undefined;
    usageLimit: number;
    termLimit: number;
    linkTo?: LocationDescriptor;
};

const buildUsageConditionString = (usageLimit: number, termLimit: number, t: TFunction, separator = '|'): string => {
    const usageLimitElement = mapUsageLimit(usageLimit, t);
    const termLimitElement = mapTermLimit(termLimit, t);
    if (!usageLimit && !termLimit) {
        return t('components.productUsageConditions.noLoanOrTermLimit');
    } else if (!usageLimit) {
        return termLimitElement;
    } else if (!termLimit) {
        return usageLimitElement;
    } else {
        return `${usageLimitElement} ${separator} ${termLimitElement}`;
    }
};

const buildUsageConditionFilterString = (
    lendingModel: LendingModel | undefined,
    usageLimit: number,
    termLimit: number
): string => {
    return `${lendingModel}-${usageLimit}-${termLimit}`;
};

const buildUsageCondition = (uc: UsageConditions, showDetails: boolean, t: TFunction): JSX.Element => {
    const buildLendingModelLabel = (t: TFunction) => {
        switch (uc.lendingModel) {
            case 'OCOU':
                return (
                    <StUsageConditionLine>
                        <i>{`${t('lendingModel.ocou')}:`}</i>
                        {` ${t('components.productUsageConditions.ocou')}`}
                    </StUsageConditionLine>
                );
            case 'SIM':
                return (
                    <StUsageConditionLine>
                        <i>
                            {uc.usageLimit === UNLIMITED_USAGE_LIMIT
                                ? t('shop.subscriptions.unlimited')
                                : t('shop.subscriptions.sim', {
                                      usageLimit: uc.usageLimit
                                  })}
                            :
                        </i>{' '}
                        {t('components.productUsageConditions.simultaneousAccess')}
                    </StUsageConditionLine>
                );
            default:
                return <StUsageConditionLine>-</StUsageConditionLine>;
        }
    };

    const buildLendingDetails = (t: TFunction) => {
        if (isNil(uc.usageLimit) || isNil(uc.termLimit)) {
            return null;
        }

        const line = buildUsageConditionString(uc.usageLimit, uc.termLimit, t);
        if (!showDetails && uc.linkTo) {
            return (
                <StUsageConditionLine>
                    <Link to={uc.linkTo}>{line}</Link>
                </StUsageConditionLine>
            );
        } else {
            return <StUsageConditionLine>{line}</StUsageConditionLine>;
        }
    };

    const key = `${uc.lendingModel}-${uc.usageLimit}-${uc.termLimit}`;

    return (
        <StUsageConditionElement key={key} data-testid="usage-condition-line">
            {buildLendingModelLabel(t)}
            {buildLendingDetails(t)}
        </StUsageConditionElement>
    );
};

const getUsageConditions = (product, loanFormatKey, language, showSelectedPrice: boolean): Array<UsageConditions> => {
    if (showSelectedPrice && product.price && product.price.lendingModel) {
        const termLimit = product?.subscriptionPeriod?.lengthInDays || product.price.termLimit;

        const usageConditionString = buildUsageConditionFilterString(
            product.price.lendingModel,
            product.price.usageLimit,
            termLimit
        );
        const linkTo = setUsageConditionFilterLink(product.productClass, loanFormatKey, usageConditionString, language);
        return [
            {
                lendingModel: product.price.lendingModel,
                usageLimit: product.price.usageLimit,
                termLimit,
                linkTo
            }
        ];
    }

    if (product.availablePrices && product.availablePrices.length > 0) {
        const sortedPrices = sortBy(product.availablePrices, ['lendingModel', 'usageLimit', 'termLimit']);
        return sortedPrices.map(ap => {
            const usageConditionString = buildUsageConditionFilterString(ap.lendingModel, ap.usageLimit, ap.termLimit);
            const linkTo = setUsageConditionFilterLink(
                product.productClass,
                loanFormatKey,
                usageConditionString,
                language
            );
            return { lendingModel: ap.lendingModel, usageLimit: ap.usageLimit, termLimit: ap.termLimit, linkTo };
        });
    }

    const usageLimit = product.usageLimit;
    const termLimit = product.termLimit;
    const lendingModel = getEffectiveLendingModel(product);
    const usageConditionString = buildUsageConditionFilterString(lendingModel, usageLimit, termLimit);
    const linkTo = setUsageConditionFilterLink(product.productClass, loanFormatKey, usageConditionString, language);

    const usageCondition = { lendingModel, usageLimit, termLimit, linkTo };
    return [usageCondition];
};

export const ProductUsageConditions = props => {
    const { t } = useTranslation();
    const { product, showDetails } = props;
    if (!product || (!showDetails && props.preventCampaignBadge)) return null;

    const language = props.language;
    const loanFormatKey = props.loanFormatKey;
    const showSelectedPrice = props.showSelectedPrice;

    return (
        <StUsageConditionContainer className="product-usage-conditions">
            {getUsageConditions(product, loanFormatKey, language, showSelectedPrice).map(uc =>
                buildUsageCondition(uc, showDetails, t)
            )}
        </StUsageConditionContainer>
    );
};

export default compose(withLanguageContext, withCampaignContext)(ProductUsageConditions);
export const __usageConditionsHelperFunctions = {
    mapTermLimit,
    mapUsageLimit
};

ProductUsageConditions.propTypes = {
    product: PropTypes.object,
    showDetails: PropTypes.bool,
    loanFormatKey: PropTypes.oneOf([
        LOANFORMAT_EBOOK_OBJECT.key,
        LOANFORMAT_EAUDIOBOOK_OBJECT.key,
        LOANFORMAT_EPRESS_OBJECT.key,
        LOANFORMAT_ALL_OBJECT.key
    ]).isRequired,
    preventCampaignBadge: PropTypes.bool,
    showSelectedPrice: PropTypes.bool,
    language: PropTypes.string
};

ProductUsageConditions.defaultProps = {
    showDetails: false
};

/**
 * Determines the effective lending model for a single product-like object.
 */
function getEffectiveLendingModel({ lendingModel }) {
    if (typeof lendingModel === 'string') {
        switch (lendingModel) {
            case LENDING_MODEL_OCOU:
            case LENDING_MODEL_SIM:
                return lendingModel;
            default:
                // if we get here, we have encountered an unknown lending model,
                // so it's better to return undefined instead of letting the fallback logic kick in,
                // which would cause the wrong lending model to be displayed
                return undefined;
        }
    }

    return undefined;
}
